import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LinkIcon from "@mui/icons-material/Link";
import { MONO_FONT } from "../../components/metrics/metricsUi";
import {
  SECTION_LINKS,
  SEVERITY_LABELS,
  ageLabel,
  fmtUnits,
  impactCaption,
  impactText,
} from "../../utils/commandCenterPageDefaults";
import DetailTable, { detailRows } from "./DetailTables";

const detailLabel = (item, count) => {
  if (item.signal === "orders") {
    return item.impact.orders > count ? `Show oldest ${count} of ${fmtUnits(item.impact.orders)} orders` : `Show ${count} orders`;
  }
  return `Show ${count} SKU${count === 1 ? "" : "s"}`;
};

const linkLabel = (path) => Object.values(SECTION_LINKS).find((link) => link.path === path)?.label || "insight";

/**
 * One thing that needs attention: what, why now, how much, what to do and
 * where to go. `relatedTitles` maps item keys to titles for the cross-links.
 */
const AttentionItem = ({ ui, item, relatedTitles, highlighted, onOpenLink, onJumpTo }) => {
  const { colors } = ui;
  const [open, setOpen] = useState(false);
  const rows = detailRows(item);
  const today = item.severity === "today";
  const accent = today ? ui.danger : ui.warning;
  const related = (item.related || []).filter((key) => relatedTitles.has(key));

  return (
    <Box
      id={`cc-${item.key}`}
      component="article"
      aria-label={item.title}
      sx={{
        border: `1px solid ${highlighted ? ui.accent : colors.border}`,
        borderLeft: `4px solid ${accent}`,
        borderRadius: "10px",
        p: "14px 16px 10px",
        scrollMarginTop: 96,
        boxShadow: highlighted ? `0 0 0 3px ${ui.accentSoft}` : "none",
        transition: "box-shadow 300ms ease, border-color 300ms ease",
      }}
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography component="h3" sx={{ fontSize: 15, fontWeight: 650, color: colors.textPrimary }}>
              {item.title}
            </Typography>
            <Box
              component="span"
              sx={{ fontSize: 11, fontWeight: 700, px: 0.9, py: 0.15, borderRadius: "10px", color: accent, backgroundColor: today ? ui.dangerBg : ui.warningBg }}
            >
              {SEVERITY_LABELS[item.severity]}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                px: 0.9,
                py: 0.15,
                borderRadius: "10px",
                color: item.is_new ? ui.accent : colors.textSecondary,
                backgroundColor: item.is_new ? ui.accentSoft : ui.flatBg,
              }}
            >
              {ageLabel(item)}
            </Box>
          </Box>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary, mt: 0.45 }}>{item.headline}</Typography>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mt: 0.9 }}>
            <ArrowForwardIcon sx={{ fontSize: 15, color: ui.accent, mt: "2px" }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>{item.action?.text}</Typography>
          </Box>
          {item.note ? <Typography sx={{ fontSize: 12, color: ui.warning, mt: 0.6 }}>{item.note}</Typography> : null}
          {item.signal === "stock" && item.facts?.blocked_orders ? (
            <Typography sx={{ fontSize: 12, color: colors.textSecondary, mt: 0.6 }}>
              Blocking {fmtUnits(item.facts.blocked_orders)} late customer order{item.facts.blocked_orders === 1 ? "" : "s"}
            </Typography>
          ) : null}
          {related.length ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.9 }}>
              <LinkIcon sx={{ fontSize: 14, color: colors.textTertiary }} />
              {related.map((key) => (
                <Box
                  key={key}
                  component="button"
                  type="button"
                  onClick={() => onJumpTo(key)}
                  sx={{
                    border: `1px solid ${colors.border}`,
                    background: "none",
                    borderRadius: "10px",
                    px: 1,
                    py: 0.2,
                    fontSize: 11.5,
                    fontFamily: "inherit",
                    color: colors.textSecondary,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: ui.surfaceAlt, color: ui.accent },
                  }}
                >
                  {relatedTitles.get(key)}
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography sx={{ fontFamily: MONO_FONT, fontSize: 18, fontWeight: 650, color: colors.textPrimary, fontVariantNumeric: "tabular-nums" }}>
            {impactText(item)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: colors.textTertiary }}>{impactCaption(item)}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mt: 1, ml: -0.75, flexWrap: "wrap" }}>
        {rows.length ? (
          <Button
            size="small"
            onClick={() => setOpen((value) => !value)}
            endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            aria-expanded={open}
            sx={{ textTransform: "none", fontSize: 12.5, color: colors.textSecondary }}
          >
            {open ? "Hide details" : detailLabel(item, rows.length)}
          </Button>
        ) : null}
        {item.link?.path ? (
          <Button
            size="small"
            onClick={() => onOpenLink(item.link)}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: "none", fontSize: 12.5, color: ui.accent }}
          >
            Open in {linkLabel(item.link.path)}
          </Button>
        ) : null}
      </Box>

      {open ? (
        <Box sx={{ mt: 0.75, mb: 0.75, overflowX: "auto" }}>
          <DetailTable ui={ui} item={item} />
        </Box>
      ) : null}
    </Box>
  );
};

export default AttentionItem;
